import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeOff, Loader2, AlertTriangle } from 'lucide-react';
import { useVisibilitySettings, useUpdateVisibility, usePassportProfile, useUpdatePassportRoles, type VisibilitySettings } from '@/hooks/useConnectApi';

const PASSPORT_ROLE_OPTIONS = [
  { value: 'technician', label: 'Technician' },
  { value: 'advisor', label: 'Advisor / Service Writer' },
  { value: 'manager', label: 'Manager' },
  { value: 'student', label: 'Student' },
] as const;

interface FormValues {
  visible_to_shops: boolean;
  share_notes_with_shops: boolean;
  open_to_work_status: 'seeking' | 'quietly_open' | 'not_seeking';
  location_zip: string;
  radius_miles: number;
  passport_roles: string[];
}

export function DiscoverabilityControls() {
  const { data: settings, isLoading: settingsLoading } = useVisibilitySettings();
  const { data: passportProfile, isLoading: profileLoading } = usePassportProfile();
  const updateMutation = useUpdateVisibility();
  const updateRolesMutation = useUpdatePassportRoles();

  const isLoading = settingsLoading || profileLoading;

  const form = useForm<FormValues>({
    defaultValues: {
      visible_to_shops: false,
      share_notes_with_shops: false,
      open_to_work_status: 'not_seeking',
      location_zip: '',
      radius_miles: 25,
      passport_roles: [],
    },
  });

  useEffect(() => {
    if (settings || passportProfile) {
      form.reset({
        visible_to_shops: settings?.visible_to_shops ?? false,
        share_notes_with_shops: settings?.share_notes_with_shops ?? false,
        open_to_work_status: settings?.open_to_work_status ?? 'not_seeking',
        location_zip: settings?.location_zip || '',
        radius_miles: settings?.radius_miles ?? 25,
        passport_roles: passportProfile?.roles || (settings as any)?.passport_roles || [],
      });
    }
  }, [settings, passportProfile]);

  const onSubmit = async (values: FormValues) => {
    // Update roles in passport_profiles (canonical source)
    if (passportProfile) {
      updateRolesMutation.mutate(values.passport_roles);
    }

    // Update visibility settings (without passport_roles — they live in passport_profiles now)
    updateMutation.mutate({
      visible_to_shops: values.visible_to_shops,
      share_notes_with_shops: values.share_notes_with_shops,
      open_to_work_status: values.open_to_work_status,
      location_zip: values.location_zip || null,
      radius_miles: values.radius_miles,
    } as Partial<VisibilitySettings>);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const visible = form.watch('visible_to_shops');
  const roles = form.watch('passport_roles');
  const zip = form.watch('location_zip');

  // Inline validation for visibility requirements
  const missingZip = visible && !zip?.trim();
  const missingRoles = visible && (!roles || roles.length === 0);
  const canSave = !visible || (!missingZip && !missingRoles);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          {visible ? <Eye className="h-5 w-5 text-primary" /> : <EyeOff className="h-5 w-5 text-muted-foreground" />}
          <CardTitle className="text-lg">Discoverability</CardTitle>
        </div>
        <CardDescription>
          Control whether shops can find your profile. Your toolbox is never shared.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* Visible toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="visible_to_shops">Visible to recruiting shops</Label>
            <Switch
              id="visible_to_shops"
              checked={form.watch('visible_to_shops')}
              onCheckedChange={(v) => form.setValue('visible_to_shops', v)}
            />
          </div>

          {visible && (
            <>
              {/* Your Roles */}
              <div className="space-y-2">
                <Label>Your Roles</Label>
                <p className="text-xs text-muted-foreground">Select the roles you're qualified for or interested in.</p>
                {PASSPORT_ROLE_OPTIONS.map((role) => {
                  return (
                    <div key={role.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`passport-role-${role.value}`}
                        checked={roles.includes(role.value)}
                        onCheckedChange={(checked) => {
                          const current = form.getValues('passport_roles');
                          form.setValue(
                            'passport_roles',
                            checked
                              ? [...current, role.value]
                              : current.filter((r) => r !== role.value)
                          );
                        }}
                      />
                      <Label htmlFor={`passport-role-${role.value}`} className="font-normal cursor-pointer">
                        {role.label}
                      </Label>
                    </div>
                  );
                })}
                {missingRoles && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Select at least one role to be visible to shops
                  </p>
                )}
              </div>

              {/* Open to work status */}
              <div className="space-y-1.5">
                <Label>Open-to-work status</Label>
                <Select
                  value={form.watch('open_to_work_status')}
                  onValueChange={(v) => form.setValue('open_to_work_status', v as FormValues['open_to_work_status'])}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seeking">Actively Seeking</SelectItem>
                    <SelectItem value="quietly_open">Quietly Open</SelectItem>
                    <SelectItem value="not_seeking">Not Seeking</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ZIP */}
              <div className="space-y-1.5">
                <Label>Your ZIP code</Label>
                <Input
                  placeholder="e.g. 90210"
                  maxLength={10}
                  {...form.register('location_zip')}
                />
                {missingZip ? (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    ZIP code is required to be visible to shops
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">Required when visible. Only used for proximity matching — never shared with shops.</p>
                )}
              </div>

              {/* Radius */}
              <div className="space-y-1.5">
                <Label>Search radius (miles)</Label>
                <Input
                  type="number"
                  min={5}
                  max={250}
                  {...form.register('radius_miles', { valueAsNumber: true })}
                />
              </div>

              {/* Share notes */}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="share_notes">Share coaching notes</Label>
                  <p className="text-xs text-muted-foreground">Allow shops to see your coaching notes</p>
                </div>
                <Switch
                  id="share_notes"
                  checked={form.watch('share_notes_with_shops')}
                  onCheckedChange={(v) => form.setValue('share_notes_with_shops', v)}
                />
              </div>
            </>
          )}

          <Button type="submit" disabled={updateMutation.isPending || !canSave} className="w-full">
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Settings
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
